import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import ApiService from '../../../services/ApiService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { formatDate } from '../../../utils/helper';

const BusinessTypeList = () => {
  const [isLoader, setIsLoader] = useState(false);
  const [businessTypes, setBusinessTypes] = useState([]);
  const [pagination, setPagination] = useState({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => { fetchList(); }, [page, search]);

  const fetchList = async () => {
    try {
      setIsLoader(true);
      const res = await ApiService.request({
        method: 'GET',
        url: 'admin/business-types',
        data: { search, page, limit: 20 },
      });
      if (res.data.status) {
        setBusinessTypes(res.data.data);
        setPagination(res.data.pagination);
      } else {
        toast.error(res.data.message);
      }
    } catch (_) {}
    finally { setIsLoader(false); }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Business Type?',
      text: 'This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      confirmButtonText: 'Yes, delete!',
      cancelButtonColor: '#9E9B9B',
    });
    if (!result.isConfirmed) return;
    const res = await ApiService.request({ method: 'DELETE', url: `admin/business-types/${id}` });
    if (res.data.status) { toast.success(res.data.message); fetchList(); }
    else toast.error(res.data.message);
  };

  const handleToggleStatus = async (id) => {
    const result = await Swal.fire({
      title: 'Change status?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#4BC95F',
      confirmButtonText: 'Yes',
      cancelButtonColor: '#9E9B9B',
    });
    if (!result.isConfirmed) return;
    const res = await ApiService.request({ method: 'POST', url: `admin/business-types/status/${id}` });
    if (res.data.status) { toast.success(res.data.message); fetchList(); }
    else toast.error(res.data.message);
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
              Business Types
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
              </li>
              <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
              <li className="breadcrumb-item text-muted">Business Types</li>
            </ul>
          </div>
          <Link to="/admin/business-types/create" className="btn btn-base btn-sm">
            Add New Business Type
          </Link>
        </div>
      </div>

      <div id="kt_app_content" className="app-content flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          <div className="card">
            <div className="card-header border-0 pt-6">
              <div className="w-100">
                <div className="row">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control form-control-solid"
                      placeholder="Search business type…"
                      value={search}
                      onChange={e => { setSearch(e.target.value); setPage(1); }}
                    />
                  </div>
                  {search && (
                    <div className="col-md-2 mt-2">
                      <button className="btn btn-sm btn-secondary" onClick={() => { setSearch(''); setPage(1); }}>
                        Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="card-body pt-0">
              <div className="table-responsive">
                <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer">
                  <thead>
                    <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                      <th>#</th>
                      <th>Title</th>
                      <th>Title Arabic</th>
                      <th>Created Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="fw-semibold text-gray-600 fs-6">
                    {isLoader ? (
                      <tr><td colSpan="6" className="text-center py-5"><Loader text="Loading" size={30} /></td></tr>
                    ) : businessTypes.length === 0 ? (
                      <tr><td colSpan="6" className="text-center text-dark mt-2">No data found!</td></tr>
                    ) : businessTypes.map((bt, i) => (
                      <tr key={bt._id}>
                        <td>{(page - 1) * 20 + i + 1}</td>
                        <td className="fw-bold text-dark">{bt.title}</td>
                        <td>{bt.title_ar || '—'}</td>
                        <td>{formatDate(bt.createdAt, 'DD MMM YYYY, hh:mm A')}</td>
                        <td>
                          <span
                            className={`badge ${bt.status ? 'badge-light-success' : 'badge-light-danger'}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleToggleStatus(bt._id)}
                          >
                            {bt.status ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <Link to={`/admin/business-types/create/${bt._id}`} className="btn btn-sm h-35px btn-light-primary me-1">
                            <i className="bi bi-pencil-fill fs-6"></i>
                          </Link>
                          <button onClick={() => handleDelete(bt._id)} className="btn btn-sm h-35px btn-light-danger">
                            <i className="bi bi-trash-fill fs-6"></i>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <span className="text-muted fs-7">Total Records: {pagination?.total_records || 0}</span>

              {pagination.total_pages > 1 && (
                <Paginator
                  page={pagination.current_page}
                  rows={pagination.per_page}
                  totalRecords={pagination.total_records}
                  onPageChange={setPage}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessTypeList;
